import React, { useState } from "react";
import { toast } from "sonner";
import { Coins, LogIn } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const QUICK_AMOUNTS = [0, 500, 1000, 2000, 5000];

// Modal that blocks the POS until the cashier records their opening drawer cash.
// Cannot be dismissed — only submitted. (`open` is fully controlled by parent.)
export default function OpenShiftDialog({ open, onOpened }) {
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (amount === "") { toast.error("Enter opening cash (0 if drawer is empty)"); return; }
    const n = Number(amount);
    if (Number.isNaN(n) || n < 0) { toast.error("Opening cash must be 0 or higher"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/pos/shift/open", { opening_cash: n });
      toast.success(`Shift opened · Drawer ₱${n.toFixed(2)}`);
      onOpened(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* blocked — must submit */ }}>
      <DialogContent
        className="max-w-md [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="open-shift-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Coins className="h-6 w-6 text-[#E67E22]" />
            Open your shift
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#4A5D5A] -mt-2">
          How much <b>cash</b> is in the drawer right now? This is your starting
          balance — used to reconcile cash at the end of the shift.
        </p>

        <div className="space-y-2">
          <Label htmlFor="opening-cash" className="text-xs">Opening cash (₱)</Label>
          <Input
            id="opening-cash"
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 500.00"
            className="h-12 text-2xl font-heading"
            autoFocus
            data-testid="opening-cash-input"
          />
          <div className="grid grid-cols-5 gap-1.5 pt-1">
            {QUICK_AMOUNTS.map((q) => (
              <button
                key={q}
                type="button"
                onClick={() => setAmount(String(q))}
                className="h-10 rounded-lg border border-[#E1E5DF] bg-[#F1F3EE] hover:bg-[#E1E5DF] text-sm font-semibold"
                data-testid={`opening-cash-chip-${q}`}
              >
                ₱{q}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-[#FFF8E1] border border-[#E67E22] rounded-lg p-3 text-xs text-[#7A4F1A]">
          You will <b>not be able to ring up sales</b> until your shift is open.
          When you close the shift later, the app will tell you whether your
          drawer balances.
        </div>

        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full h-12 bg-[#2F5233] hover:bg-[#1F3D24] text-white text-base font-bold"
          data-testid="open-shift-submit"
        >
          <LogIn className="h-5 w-5 mr-2" />
          {submitting ? "Opening…" : "Open shift"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
